import { FormInputField, FormInputFieldStatus } from '../../../contracts/form-input-field.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { cloneData, isSameData } from '../../dto/data-model-utils.js';
import { LocalizedString } from '../localized-string.js';
import { VersionRange } from '../../core/version-range.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';

export class BooleanFormInputField extends FormInputField {
  static readonly TYPE_ID: string = 'f755b4563e3f4161964f1ba9ed46ccdf';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly uniqueId: string,
    public readonly titles: LocalizedString,
    public readonly descriptions: LocalizedString,
    public readonly isRequired: boolean,
    public readonly status: FormInputFieldStatus,
  ) {
    super();
  }

  get $typeId(): string {
    return BooleanFormInputField.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, BooleanFormInputField.SCHEMA_VERSION);
  }

  static create(args: {
    uniqueId: string,
    titles: LocalizedString,
    descriptions: LocalizedString,
    isRequired: boolean,
    status: FormInputFieldStatus,
  }): BooleanFormInputField {
    return new BooleanFormInputField(
        args.uniqueId,
        args.titles,
        args.descriptions,
        args.isRequired,
        args.status,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): BooleanFormInputField {
    return BooleanFormInputField.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      titles: context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'titles')),
      descriptions: context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'descriptions')),
      isRequired: MapUtils.getBoolean(map, 'isRequired'),
      status: toEnumFromString(MapUtils.getString(map, '$status'), FormInputFieldStatus),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'titles': context.toData(this.titles),
      'descriptions': context.toData(this.descriptions),
      'isRequired': this.isRequired,
      '$status': fromEnumAsString(this.status),
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    uniqueId = this.uniqueId,
    titles = cloneData(this.titles),
    descriptions = cloneData(this.descriptions),
    isRequired = this.isRequired,
    status = this.status,
  }: {
    uniqueId?: string,
    titles?: LocalizedString,
    descriptions?: LocalizedString,
    isRequired?: boolean,
    status?: FormInputFieldStatus,
  } = {}): this {
    return BooleanFormInputField.create({
      uniqueId,
      titles,
      descriptions,
      isRequired,
      status,
    }) as this;
  }

  isSame(other: BooleanFormInputField): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      isSameData(other.titles, this.titles) &&
      isSameData(other.descriptions, this.descriptions) &&
      other.isRequired === this.isRequired &&
      other.status === this.status;
  }
}

export class BooleanFormInputFieldBuilder extends DataModelBuilder<BooleanFormInputField> {
  build(context: SerializationContext, map: SerializedData): BooleanFormInputField {
    return BooleanFormInputField.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(_context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(BooleanFormInputField.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(BooleanFormInputField.SCHEMA_VERSION)) {
      return null;
    }

    let version = versionRange.to;

    // In version 2 we added `$status` of type `FormInputFieldStatus`.
    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, '$status', FormInputFieldStatus.Live);
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
